import type { BookWriter, WriterChoice } from "../api";

/**
 * A book's writers as the studio edits them (0118 W2): one list, authors then
 * translators, each group in its order. Every edit returns a new list, which
 * is sent whole as the book's `writers` — the server numbers each role from
 * the order it is given.
 */
export interface WriterEntry {
  /** Empty for a name the server is to find or create. */
  id: string;
  name: string;
  role: BookWriter["role"];
}

const roles: BookWriter["role"][] = ["author", "translator"];

/** grouped puts authors before translators, keeping each group's order. */
function grouped(list: WriterEntry[]): WriterEntry[] {
  return roles.flatMap((role) => list.filter((w) => w.role === role));
}

/** entriesOf is a book's writers, authors first, each role by position. */
export function entriesOf(writers: BookWriter[] | undefined): WriterEntry[] {
  const sorted = [...(writers ?? [])].sort((a, b) => a.position - b.position);
  return grouped(sorted.map((w) => ({ id: w.id, name: w.name, role: w.role })));
}

export function choicesOf(list: WriterEntry[]): WriterChoice[] {
  return list.map((w) => (w.id ? { id: w.id, role: w.role } : { name: w.name, role: w.role }));
}

/** move swaps an entry with its neighbour in the same role; else nothing. */
export function move(list: WriterEntry[], index: number, by: -1 | 1): WriterEntry[] {
  const here = list[index];
  const there = list[index + by];
  if (!here || !there || here.role !== there.role) return list;
  const next = [...list];
  next[index] = there;
  next[index + by] = here;
  return next;
}

/** canMove says whether move would do anything, for the arrows' disabled state. */
export function canMove(list: WriterEntry[], index: number, by: -1 | 1): boolean {
  return move(list, index, by) !== list;
}

/** withRole gives an entry another role, last in that role. */
export function withRole(list: WriterEntry[], index: number, role: BookWriter["role"]) {
  const here = list[index];
  if (!here || here.role === role) return list;
  const entry: WriterEntry = { ...here, role };
  return grouped([...list.slice(0, index), ...list.slice(index + 1), entry]);
}

export function without(list: WriterEntry[], index: number): WriterEntry[] {
  return list.filter((_, i) => i !== index);
}

/** withAdded appends an entry last in its role; a writer already there in that role is not added twice. */
export function withAdded(list: WriterEntry[], entry: WriterEntry): WriterEntry[] {
  if (entry.id && list.some((w) => w.id === entry.id && w.role === entry.role)) return list;
  return grouped([...list, entry]);
}
